import { Calendar, CheckCircle, Clock, Settings, Timer } from "lucide-react";
import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useAssignTemplateToWorker,
  useSwitchWorkerTemplate,
  useUpdateCustomWorkerPlan,
} from "@/domains/shifts/hooks/use-shift-mutations";
import type { CustomScheduleInput, Worker } from "@/gql/graphql";
import { CustomScheduleBuilder } from "./custom-schedule-builder";

interface EditWorkerPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: any;
  worker: Worker;
  templates: any[];
  onSuccess?: () => void;
}

export function EditWorkerPlanDialog({
  open,
  onOpenChange,
  plan,
  worker,
  templates,
  onSuccess,
}: EditWorkerPlanDialogProps) {
  const [activeTab, setActiveTab] = useState<"details" | "modify">("details");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [customSchedule, setCustomSchedule] = useState<CustomScheduleInput>({
    scheduleType: "weekly",
    days: [1, 2, 3, 4, 5],
    timeIntervals: [
      {
        startTime: "09:00",
        endTime: "17:00",
      },
    ],
    breakRules: [],
  });

  const { execute: assignTemplate, isLoading: assignLoading } = useAssignTemplateToWorker();
  const { execute: switchTemplate, isLoading: switchLoading } = useSwitchWorkerTemplate();
  const { execute: updateCustomPlan, isLoading: updateLoading } = useUpdateCustomWorkerPlan();

  // Initialize form data when plan changes
  useEffect(() => {
    if (!plan) return;

    if (plan.planType === "TEMPLATE") {
      setSelectedTemplateId(plan.templateId);
      setActiveTab("details");
    } else if (plan.planType === "CUSTOM") {
      setCustomSchedule({
        scheduleType: plan.customScheduleType || "weekly",
        days: plan.customDays || [1, 2, 3, 4, 5],
        timeIntervals: plan.customTimeIntervals?.map((interval: any) => ({
          startTime: interval.startTime,
          endTime: interval.endTime,
        })) || [
          {
            startTime: "09:00",
            endTime: "17:00",
          },
        ],
        breakRules:
          plan.customBreakRules?.map((rule: any) => ({
            afterHours: rule.afterHours,
            breakType: rule.breakType,
            duration: rule.duration,
            isPaid: rule.isPaid || false,
            isRequired: rule.isRequired !== false,
          })) || [],
      });
      setActiveTab("details");
    }
  }, [plan]);

  const handleUpdateCustomPlan = async () => {
    if (!plan?.id) return;

    try {
      await updateCustomPlan(plan.id, customSchedule);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to update custom plan:", error);
    }
  };

  const handleSwitchToTemplate = async () => {
    if (!plan?.workerSalonId || !selectedTemplateId) return;

    try {
      if (plan?.id) {
        // Worker already has a plan, use switch template to delete first then assign
        await switchTemplate(plan.id, selectedTemplateId, plan.workerSalonId);
      } else {
        // No existing plan, just assign the template
        await assignTemplate(selectedTemplateId, plan.workerSalonId);
      }
      onSuccess?.();
    } catch (error) {
      console.error("Failed to switch to template:", error);
    }
  };

  const getDayNames = (days: number[]) => {
    const dayNames = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return days.map((d) => dayNames[d]).join(", ");
  };

  const getTimeRange = (intervals: any[]) => {
    if (!intervals || intervals.length === 0) return "No time specified";
    if (intervals.length === 1) {
      return `${intervals[0].startTime} - ${intervals[0].endTime}`;
    }
    return `${intervals[0].startTime} - ${intervals[intervals.length - 1].endTime} (${intervals.length} intervals)`;
  };

  const _selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  if (!plan) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="w-8 h-8">
              <AvatarImage src={worker.profileImageUrl ?? undefined} />
              <AvatarFallback className="text-xs">
                {worker.user?.firstName?.[0] || "U"}
                {worker.user?.lastName?.[0] || "N"}
              </AvatarFallback>
            </Avatar>
            <div>
              <div>
                Edit Plan: {worker.user?.firstName} {worker.user?.lastName}
              </div>
              <div className="text-sm font-normal text-muted-foreground">{worker.user?.email}</div>
            </div>
          </DialogTitle>
          <DialogDescription>
            Modify this worker's schedule plan or switch between template and custom modes.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as "details" | "modify")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details">Current Plan</TabsTrigger>
            <TabsTrigger value="modify">Modify Plan</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            {/* Current Plan Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  {plan.planType === "TEMPLATE" ? (
                    <>
                      <Clock className="w-4 h-4 text-blue-500" />
                      <span>Template Plan</span>
                      <Badge
                        variant="outline"
                        className="text-xs bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/50 dark:border-blue-800 dark:text-blue-300"
                      >
                        Template
                      </Badge>
                    </>
                  ) : (
                    <>
                      <Settings className="w-4 h-4 text-orange-500" />
                      <span>Custom Plan</span>
                      <Badge
                        variant="outline"
                        className="text-xs bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-950/50 dark:border-orange-800 dark:text-orange-300"
                      >
                        Custom
                      </Badge>
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {plan.planType === "TEMPLATE" && plan.template ? (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: plan.template.color || "#6b7280" }}
                      />
                      {plan.template.name}
                    </h4>
                    {plan.template.description && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {plan.template.description}
                      </p>
                    )}

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground text-xs mb-1">Schedule Type</div>
                        <div className="font-medium">{plan.template.scheduleType}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs mb-1">Working Days</div>
                        <div className="font-medium">{getDayNames(plan.template.days)}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-muted-foreground text-xs mb-1">Time Intervals</div>
                        <div className="font-medium">
                          {getTimeRange(plan.template.timeIntervals)}
                        </div>
                      </div>
                    </div>

                    {plan.template.breakRules && plan.template.breakRules.length > 0 && (
                      <div>
                        <div className="text-muted-foreground text-xs mb-2">Break Rules</div>
                        <div className="space-y-1">
                          {plan.template.breakRules.map((rule: any, index: number) => (
                            <div key={index} className="text-sm bg-muted/50 rounded px-2 py-1">
                              After {rule.afterHours}h: {rule.duration}min{" "}
                              {rule.breakType.toLowerCase()}
                              {rule.isPaid && " (paid)"}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : plan.planType === "CUSTOM" ? (
                  <div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground text-xs mb-1">Schedule Type</div>
                        <div className="font-medium">{plan.customScheduleType}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs mb-1">Working Days</div>
                        <div className="font-medium">{getDayNames(plan.customDays || [])}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-muted-foreground text-xs mb-1">Time Intervals</div>
                        <div className="font-medium">{getTimeRange(plan.customTimeIntervals)}</div>
                      </div>
                    </div>

                    {plan.customBreakRules && plan.customBreakRules.length > 0 && (
                      <div>
                        <div className="text-muted-foreground text-xs mb-2">Break Rules</div>
                        <div className="space-y-1">
                          {plan.customBreakRules.map((rule: any, index: number) => (
                            <div key={index} className="text-sm bg-muted/50 rounded px-2 py-1">
                              After {rule.afterHours}h: {rule.duration}min{" "}
                              {rule.breakType.toLowerCase()}
                              {rule.isPaid && " (paid)"}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </CardContent>
            </Card>

            {/* Plan Info */}
            <Card
              className={`border-2 ${plan.planType === "TEMPLATE" ? "border-blue-200 bg-blue-50/30 dark:border-blue-800 dark:bg-blue-950/20" : "border-orange-200 bg-orange-50/30 dark:border-orange-800 dark:bg-orange-950/20"}`}
            >
              <CardContent className="pt-4">
                {plan.planType === "TEMPLATE" ? (
                  <div className={"text-sm text-blue-800 dark:text-blue-200"}>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium">Template Plan Benefits</span>
                    </div>
                    <ul className="space-y-1 text-xs ml-6">
                      <li>• Automatically updates when template changes</li>
                      <li>• Consistent with other workers using this template</li>
                      <li>• Easy to manage centrally</li>
                    </ul>
                  </div>
                ) : (
                  <div className={"text-sm text-orange-800 dark:text-orange-200"}>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium">Custom Plan Benefits</span>
                    </div>
                    <ul className="space-y-1 text-xs ml-6">
                      <li>• Completely independent schedule</li>
                      <li>• Perfect for unique worker needs</li>
                      <li>• Full control over all details</li>
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="modify" className="space-y-4">
            {plan.planType === "CUSTOM" ? (
              // Edit Custom Plan
              <div className="space-y-4">
                <Card className="border-orange-200 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-950/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-orange-900 dark:text-orange-100 text-sm">
                      Modify Custom Schedule
                    </CardTitle>
                    <CardDescription className="text-xs text-orange-800 dark:text-orange-200">
                      Update the schedule details for this worker
                    </CardDescription>
                  </CardHeader>
                </Card>

                <CustomScheduleBuilder value={customSchedule} onChange={setCustomSchedule} />

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpdateCustomPlan}
                    disabled={
                      updateLoading ||
                      !customSchedule.days.length ||
                      !customSchedule.timeIntervals?.length
                    }
                  >
                    {updateLoading ? "Updating..." : "Update Custom Plan"}
                  </Button>
                </div>
              </div>
            ) : (
              // Switch to Template or Change Template
              <div className="space-y-4">
                <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-blue-900 dark:text-blue-100 text-sm">
                      Switch Template
                    </CardTitle>
                    <CardDescription className="text-xs text-blue-800 dark:text-blue-200">
                      Change to a different template for this worker
                    </CardDescription>
                  </CardHeader>
                </Card>

                <div className="space-y-3">
                  <h3 className="font-medium text-sm">Choose New Template</h3>
                  <div className="grid gap-3 max-h-60 overflow-y-auto">
                    {templates.map((template) => (
                      <Card
                        key={template.id}
                        className={`cursor-pointer transition-colors ${
                          selectedTemplateId === template.id
                            ? "ring-2 ring-primary border-primary"
                            : "hover:border-primary/50"
                        }`}
                        onClick={() => setSelectedTemplateId(template.id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: template.color || "#6b7280" }}
                              />
                              <h4 className="font-medium text-sm">{template.name}</h4>
                              {template.id === plan.templateId && (
                                <Badge variant="secondary" className="text-xs">
                                  Current
                                </Badge>
                              )}
                            </div>
                            {selectedTemplateId === template.id &&
                              template.id !== plan.templateId && (
                                <CheckCircle className="w-4 h-4 text-primary" />
                              )}
                          </div>

                          {template.description && (
                            <p className="text-xs text-muted-foreground mb-2">
                              {template.description}
                            </p>
                          )}

                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>{getDayNames(template.days)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Timer className="w-3 h-3" />
                              <span>{getTimeRange(template.timeIntervals || [])}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSwitchToTemplate}
                    disabled={
                      !selectedTemplateId ||
                      selectedTemplateId === plan.templateId ||
                      assignLoading ||
                      switchLoading
                    }
                  >
                    {assignLoading || switchLoading ? "Switching..." : "Switch Template"}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
